import { useEffect } from 'react'
import { Header } from '../components/layout/Header'
import { Footer } from '../components/layout/Footer'

export const Privacy = () => {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-sm p-8 sm:p-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
              Política de Privacidade
            </h1>
            <p className="text-sm text-gray-500 mb-8">
              Última atualização: 21 de janeiro de 2026
            </p>

            <div className="prose prose-gray max-w-none">
              {/* Introdução */}
              <div className="bg-blue-50 rounded-lg p-6 mb-8">
                <p className="text-gray-700">
                  A Projetoize está comprometida com a proteção da privacidade e dos dados pessoais de seus usuários. Esta Política de Privacidade foi elaborada em conformidade com a <strong>Lei Geral de Proteção de Dados Pessoais (Lei nº 13.709/2018 - LGPD)</strong> e descreve como coletamos, utilizamos, armazenamos e protegemos suas informações.
                </p>
              </div>

              {/* Índice */}
              <nav className="bg-gray-50 rounded-lg p-6 mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Índice</h2>
                <ol className="list-decimal list-inside space-y-2 text-primary-600">
                  <li><a href="#controlador" className="hover:underline">Controlador dos Dados</a></li>
                  <li><a href="#dados-coletados" className="hover:underline">Dados Coletados</a></li>
                  <li><a href="#finalidades" className="hover:underline">Finalidades do Tratamento</a></li>
                  <li><a href="#base-legal" className="hover:underline">Base Legal para o Tratamento</a></li>
                  <li><a href="#compartilhamento" className="hover:underline">Compartilhamento de Dados</a></li>
                  <li><a href="#armazenamento" className="hover:underline">Armazenamento e Segurança</a></li>
                  <li><a href="#direitos" className="hover:underline">Direitos do Titular</a></li>
                  <li><a href="#cookies" className="hover:underline">Cookies e Tecnologias Similares</a></li>
                  <li><a href="#retencao" className="hover:underline">Retenção de Dados</a></li>
                  <li><a href="#transferencia" className="hover:underline">Transferência Internacional</a></li>
                  <li><a href="#menores" className="hover:underline">Dados de Menores</a></li>
                  <li><a href="#atualizacoes" className="hover:underline">Atualizações desta Política</a></li>
                  <li><a href="#contato" className="hover:underline">Contato e Encarregado (DPO)</a></li>
                </ol>
              </nav>

              {/* 1. Controlador dos Dados */}
              <section id="controlador" className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Controlador dos Dados</h2>
                <p className="text-gray-700 mb-4">
                  O controlador dos dados pessoais tratados por meio da Plataforma Projetoize é:
                </p>
                <div className="bg-gray-50 rounded-lg p-4 ml-4">
                  <p className="text-gray-700"><strong>Projetoize</strong></p>
                  <p className="text-gray-700">E-mail: <a href="mailto:contato@projetoize.com.br" className="text-primary-600 hover:underline">contato@projetoize.com.br</a></p>
                </div>
              </section>

              {/* 2. Dados Coletados */}
              <section id="dados-coletados" className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Dados Coletados</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.1. Dados fornecidos pelo usuário</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li><strong>Dados de cadastro:</strong> nome completo, endereço de e-mail, senha (armazenada de forma criptografada), nome da empresa/organização;</li>
                  <li><strong>Dados de perfil:</strong> informações adicionais que o usuário opte por fornecer;</li>
                  <li><strong>Conteúdo inserido:</strong> projetos, tarefas, comentários, documentos e demais informações inseridas na Plataforma;</li>
                  <li><strong>Dados de comunicação:</strong> mensagens enviadas ao suporte ou através de canais de atendimento.</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.2. Dados coletados automaticamente</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li><strong>Dados de acesso:</strong> endereço IP, tipo de navegador, sistema operacional, páginas acessadas, data e hora de acesso;</li>
                  <li><strong>Dados de uso:</strong> funcionalidades utilizadas, frequência de acesso, interações com a Plataforma;</li>
                  <li><strong>Dados de dispositivo:</strong> identificadores únicos do dispositivo, resolução de tela, idioma configurado;</li>
                  <li><strong>Cookies e tecnologias similares:</strong> conforme descrito na Seção 8.</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.3. Dados de pagamento</h3>
                <p className="text-gray-700">
                  Os dados de pagamento (número de cartão de crédito, dados bancários) são processados diretamente por nossos parceiros de pagamento e não são armazenados em nossos servidores. Podemos receber informações parciais (últimos 4 dígitos do cartão, data de expiração) apenas para fins de identificação da transação.
                </p>
              </section>

              {/* 3. Finalidades do Tratamento */}
              <section id="finalidades" className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Finalidades do Tratamento</h2>
                <p className="text-gray-700 mb-4">
                  Utilizamos seus dados pessoais para as seguintes finalidades:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li><strong>Prestação do serviço:</strong> criar e gerenciar sua conta, processar suas solicitações, fornecer acesso às funcionalidades da Plataforma;</li>
                  <li><strong>Comunicações:</strong> enviar notificações sobre sua conta, atualizações do serviço, informações de cobrança e suporte;</li>
                  <li><strong>Personalização:</strong> adaptar a experiência de uso às suas preferências;</li>
                  <li><strong>Segurança:</strong> detectar e prevenir fraudes, abusos e atividades não autorizadas;</li>
                  <li><strong>Melhorias:</strong> analisar o uso da Plataforma para desenvolver novos recursos e melhorar os existentes;</li>
                  <li><strong>Cumprimento legal:</strong> atender obrigações legais, regulatórias e requisições de autoridades competentes;</li>
                  <li><strong>Marketing:</strong> enviar comunicações promocionais, mediante consentimento prévio, podendo o usuário cancelar a qualquer momento.</li>
                </ul>
              </section>

              {/* 4. Base Legal para o Tratamento */}
              <section id="base-legal" className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Base Legal para o Tratamento</h2>
                <p className="text-gray-700 mb-4">
                  O tratamento de dados pessoais pela Projetoize fundamenta-se nas seguintes bases legais previstas na LGPD (Art. 7º):
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Finalidade</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Base Legal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-700">Criação e gestão de conta</td>
                        <td className="px-4 py-3 text-sm text-gray-700">Execução de contrato (Art. 7º, V)</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-700">Prestação do serviço</td>
                        <td className="px-4 py-3 text-sm text-gray-700">Execução de contrato (Art. 7º, V)</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-700">Processamento de pagamentos</td>
                        <td className="px-4 py-3 text-sm text-gray-700">Execução de contrato (Art. 7º, V)</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-700">Comunicações de serviço</td>
                        <td className="px-4 py-3 text-sm text-gray-700">Execução de contrato (Art. 7º, V)</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-700">Segurança e prevenção de fraudes</td>
                        <td className="px-4 py-3 text-sm text-gray-700">Legítimo interesse (Art. 7º, IX)</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-700">Análise e melhorias do serviço</td>
                        <td className="px-4 py-3 text-sm text-gray-700">Legítimo interesse (Art. 7º, IX)</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-700">Marketing direto</td>
                        <td className="px-4 py-3 text-sm text-gray-700">Consentimento (Art. 7º, I)</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-700">Cumprimento de obrigações legais</td>
                        <td className="px-4 py-3 text-sm text-gray-700">Obrigação legal (Art. 7º, II)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* 5. Compartilhamento de Dados */}
              <section id="compartilhamento" className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Compartilhamento de Dados</h2>
                <p className="text-gray-700 mb-4">
                  A Projetoize poderá compartilhar seus dados pessoais nas seguintes hipóteses:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li><strong>Prestadores de serviço:</strong> empresas contratadas para operar infraestrutura, processar pagamentos, enviar e-mails ou prestar suporte, sempre mediante contratos que garantam a proteção dos dados;</li>
                  <li><strong>Parceiros de negócio:</strong> integrações autorizadas pelo usuário com outras plataformas;</li>
                  <li><strong>Requisições legais:</strong> quando necessário para cumprir obrigação legal, ordem judicial ou requisição de autoridade competente;</li>
                  <li><strong>Proteção de direitos:</strong> quando necessário para proteger os direitos, propriedade ou segurança da Projetoize, de seus usuários ou de terceiros;</li>
                  <li><strong>Transações corporativas:</strong> em caso de fusão, aquisição ou venda de ativos, os dados poderão ser transferidos ao sucessor, mantidas as obrigações desta Política.</li>
                </ul>
                <p className="text-gray-700 mt-4">
                  <strong>Não vendemos, alugamos ou comercializamos seus dados pessoais com terceiros para fins de marketing.</strong>
                </p>
              </section>

              {/* 6. Armazenamento e Segurança */}
              <section id="armazenamento" className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Armazenamento e Segurança</h2>
                <p className="text-gray-700 mb-4">
                  6.1. Os dados são armazenados em servidores seguros, utilizando as seguintes medidas de proteção:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li><strong>Criptografia:</strong> dados sensíveis são criptografados em trânsito (TLS/SSL) e em repouso;</li>
                  <li><strong>Senhas:</strong> armazenadas utilizando algoritmos de hash seguros (nunca em texto plano);</li>
                  <li><strong>Controle de acesso:</strong> acesso aos dados restrito a funcionários autorizados que necessitam processá-los;</li>
                  <li><strong>Monitoramento:</strong> sistemas de detecção de intrusão e monitoramento contínuo;</li>
                  <li><strong>Backups:</strong> cópias de segurança regulares para garantir a recuperação dos dados;</li>
                  <li><strong>Isolamento:</strong> cada organização (Tenant) possui ambiente isolado, garantindo que dados não sejam acessados por outros usuários.</li>
                </ul>
                <p className="text-gray-700 mt-4">
                  6.2. Embora adotemos medidas de segurança adequadas, nenhum sistema é completamente imune a riscos. Em caso de incidente de segurança que possa acarretar risco ou dano relevante, notificaremos os titulares afetados e a Autoridade Nacional de Proteção de Dados (ANPD) conforme exigido pela LGPD.
                </p>
              </section>

              {/* 7. Direitos do Titular */}
              <section id="direitos" className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Direitos do Titular</h2>
                <p className="text-gray-700 mb-4">
                  De acordo com a LGPD, você possui os seguintes direitos em relação aos seus dados pessoais:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li><strong>Confirmação e acesso:</strong> confirmar a existência de tratamento e acessar seus dados;</li>
                  <li><strong>Correção:</strong> solicitar a correção de dados incompletos, inexatos ou desatualizados;</li>
                  <li><strong>Anonimização, bloqueio ou eliminação:</strong> solicitar a anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou tratados em desconformidade;</li>
                  <li><strong>Portabilidade:</strong> solicitar a portabilidade dos dados a outro fornecedor de serviço;</li>
                  <li><strong>Eliminação:</strong> solicitar a eliminação dos dados tratados com base no consentimento;</li>
                  <li><strong>Informação sobre compartilhamento:</strong> obter informações sobre entidades com as quais seus dados foram compartilhados;</li>
                  <li><strong>Revogação do consentimento:</strong> revogar o consentimento a qualquer momento;</li>
                  <li><strong>Oposição:</strong> opor-se ao tratamento realizado com base em legítimo interesse, em caso de descumprimento da LGPD;</li>
                  <li><strong>Revisão de decisões automatizadas:</strong> solicitar a revisão de decisões tomadas unicamente com base em tratamento automatizado.</li>
                </ul>
                <p className="text-gray-700 mt-4">
                  Para exercer seus direitos, entre em contato através do e-mail <a href="mailto:contato@projetoize.com.br" className="text-primary-600 hover:underline">contato@projetoize.com.br</a>. Responderemos sua solicitação no prazo de 15 (quinze) dias, conforme previsto na LGPD.
                </p>
              </section>

              {/* 8. Cookies e Tecnologias Similares */}
              <section id="cookies" className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cookies e Tecnologias Similares</h2>
                <p className="text-gray-700 mb-4">
                  8.1. A Plataforma utiliza cookies e tecnologias similares para:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li><strong>Cookies essenciais:</strong> necessários para o funcionamento básico da Plataforma, autenticação e segurança;</li>
                  <li><strong>Cookies de preferências:</strong> armazenam suas configurações e preferências (ex: tema claro/escuro);</li>
                  <li><strong>Cookies de análise:</strong> coletam informações anônimas sobre o uso da Plataforma para melhorias;</li>
                </ul>
                <p className="text-gray-700 mt-4 mb-4">
                  8.2. Você pode gerenciar as configurações de cookies através do seu navegador. Note que a desativação de cookies essenciais pode afetar o funcionamento da Plataforma.
                </p>
                <p className="text-gray-700">
                  8.3. Não utilizamos cookies para fins de publicidade comportamental ou rastreamento entre sites.
                </p>
              </section>

              {/* 9. Retenção de Dados */}
              <section id="retencao" className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Retenção de Dados</h2>
                <p className="text-gray-700 mb-4">
                  9.1. Os dados pessoais serão mantidos pelo período necessário para:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Cumprir as finalidades para as quais foram coletados;</li>
                  <li>Atender obrigações legais, contratuais, de prestação de contas ou requisição de autoridades;</li>
                  <li>Exercer direitos em processos judiciais, administrativos ou arbitrais.</li>
                </ul>
                <p className="text-gray-700 mt-4 mb-4">
                  9.2. Prazos específicos de retenção:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li><strong>Dados de conta ativa:</strong> enquanto a conta estiver ativa;</li>
                  <li><strong>Dados após cancelamento:</strong> até 90 (noventa) dias para possível reativação;</li>
                  <li><strong>Dados fiscais e de faturamento:</strong> 5 (cinco) anos, conforme legislação tributária;</li>
                  <li><strong>Logs de acesso:</strong> 6 (seis) meses, conforme Marco Civil da Internet;</li>
                  <li><strong>Dados para defesa em processos:</strong> até a prescrição do direito de ação aplicável.</li>
                </ul>
                <p className="text-gray-700 mt-4">
                  9.3. Após os prazos de retenção, os dados serão eliminados ou anonimizados de forma segura.
                </p>
              </section>

              {/* 10. Transferência Internacional */}
              <section id="transferencia" className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Transferência Internacional</h2>
                <p className="text-gray-700 mb-4">
                  10.1. Seus dados podem ser transferidos e processados em servidores localizados fora do Brasil, especialmente para fins de hospedagem em nuvem e processamento de pagamentos.
                </p>
                <p className="text-gray-700 mb-4">
                  10.2. Quando houver transferência internacional, garantimos que:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Os países de destino possuem legislação de proteção de dados adequada; ou</li>
                  <li>São adotadas cláusulas contratuais padrão aprovadas pela ANPD; ou</li>
                  <li>Existem outras garantias previstas na LGPD para assegurar a proteção dos dados.</li>
                </ul>
              </section>

              {/* 11. Dados de Menores */}
              <section id="menores" className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Dados de Menores</h2>
                <p className="text-gray-700 mb-4">
                  11.1. A Plataforma Projetoize é destinada a usuários maiores de 18 (dezoito) anos ou devidamente emancipados.
                </p>
                <p className="text-gray-700 mb-4">
                  11.2. Não coletamos intencionalmente dados de menores de idade. Caso tome conhecimento de que coletamos dados de menor sem o consentimento dos responsáveis, entre em contato conosco para que possamos adotar as medidas necessárias.
                </p>
              </section>

              {/* 12. Atualizações desta Política */}
              <section id="atualizacoes" className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Atualizações desta Política</h2>
                <p className="text-gray-700 mb-4">
                  12.1. Esta Política de Privacidade pode ser atualizada periodicamente para refletir alterações em nossas práticas, novas funcionalidades ou exigências legais.
                </p>
                <p className="text-gray-700 mb-4">
                  12.2. Notificaremos sobre alterações significativas através de:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Aviso destacado na Plataforma;</li>
                  <li>E-mail para os usuários cadastrados;</li>
                  <li>Atualização da data de "última atualização" no topo deste documento.</li>
                </ul>
                <p className="text-gray-700 mt-4">
                  12.3. Recomendamos revisar esta Política periodicamente para se manter informado sobre como protegemos seus dados.
                </p>
              </section>

              {/* 13. Contato e Encarregado */}
              <section id="contato" className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contato e Encarregado (DPO)</h2>
                <p className="text-gray-700 mb-4">
                  Para esclarecer dúvidas sobre esta Política de Privacidade, exercer seus direitos como titular de dados ou reportar preocupações relacionadas à privacidade, entre em contato com nosso Encarregado de Proteção de Dados:
                </p>
                <div className="bg-gray-50 rounded-lg p-4 ml-4">
                  <p className="text-gray-700"><strong>Encarregado de Proteção de Dados (DPO)</strong></p>
                  <p className="text-gray-700">Projetoize</p>
                  <p className="text-gray-700">E-mail: <a href="mailto:contato@projetoize.com.br" className="text-primary-600 hover:underline">contato@projetoize.com.br</a></p>
                </div>
                <p className="text-gray-700 mt-4">
                  Comprometemo-nos a responder suas solicitações no prazo legal de 15 (quinze) dias, podendo ser prorrogado por igual período em caso de necessidade, mediante justificativa.
                </p>
              </section>

              {/* Aceite */}
              <div className="bg-primary-50 rounded-lg p-6 mt-8">
                <p className="text-gray-700 font-medium">
                  Ao utilizar a Plataforma Projetoize, você declara estar ciente e concordar com esta Política de Privacidade. Em caso de discordância, solicitamos que não utilize nossos serviços.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}
